import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Leaf,
  Sparkles,
  ShoppingBasket,
  ArrowRight,
  Check,
  Droplets,
  Sun,
  Moon,
  Flame,
  Snowflake,
  Heart,
  Star,
  ShieldCheck,
  Clock3,
  Coffee,
  Info,
  ChevronDown,
  Flower2,
  Citrus,
  Zap,
  Package,
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { useApp } from "../context/AppContext";
import { formatRupiah } from "../lib/format";
import SEOHead from "../components/SEOHead";
import PublicBreadcrumbs from "../components/PublicBreadcrumbs";
import {
  buildBreadcrumbSchema,
  buildCollectionPageSchema,
  buildFaqSchema,
  buildOrganizationSchema,
  buildProductSchema,
} from "../lib/seo";
import { SEED_PRODUCTS } from "../lib/storage";

const TELANG_ID = "p1";
const ROSELA_ID = "p2";
const PAGE_PATH = "/telang-rosella";

const pageFaqs = [
  {
    q: "Apakah ini obat?",
    a: "Bukan. Ini adalah minuman herbal alami untuk mendukung gaya hidup sehat, bukan pengganti obat medis.",
  },
  {
    q: "Boleh diminum setiap hari?",
    a: "Boleh dikonsumsi secara wajar sesuai kebutuhan. Hindari gula berlebihan agar manfaat minuman tetap optimal.",
  },
  {
    q: "Rosella aman untuk semua orang?",
    a: "Rosella memiliki rasa asam dan dapat kurang cocok untuk sebagian orang dengan kondisi tertentu. Jika memiliki tekanan darah rendah, lambung sensitif, sedang hamil, menyusui, atau minum obat rutin, sebaiknya konsultasikan dengan tenaga kesehatan.",
  },
  {
    q: "Bunga telang bisa berubah warna?",
    a: "Ya, saat ditambahkan lemon atau bahan asam, warna biru telang dapat berubah menjadi ungu secara alami.",
  },
  {
    q: "Bisa diminum dingin?",
    a: "Bisa. Setelah diseduh, minuman dapat ditambahkan es batu agar lebih segar.",
  },
];

/* -------------------------------------------------------- */
/* Small reusable presentational helpers                    */
/* -------------------------------------------------------- */
const SectionHeading = ({ eyebrow, title, desc, tone = "emerald" }) => {
  const toneClass = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
    purple: "bg-purple-50 text-purple-700 border-purple-100",
    rose: "bg-rose-50 text-rose-700 border-rose-100",
  }[tone];
  return (
    <div className="max-w-3xl">
      {eyebrow && (
        <span
          className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium border ${toneClass}`}
        >
          <Sparkles className="w-4 h-4" /> {eyebrow}
        </span>
      )}
      <h2 className="mt-4 font-display text-2xl leading-tight text-stone-900 sm:text-4xl font-semibold">
        {title}
      </h2>
      {desc && (
        <p className="mt-3 text-stone-600 text-base sm:text-lg leading-relaxed">
          {desc}
        </p>
      )}
    </div>
  );
};

/* =========================================================
   THE PAGE
========================================================= */
const TelangRosellaLandingPage = () => {
  const { products, addToCart, openCart } = useApp();

  const telang =
    products.find((p) => p.id === TELANG_ID) ||
    SEED_PRODUCTS.find((p) => p.id === TELANG_ID);
  const rosela =
    products.find((p) => p.id === ROSELA_ID) ||
    SEED_PRODUCTS.find((p) => p.id === ROSELA_ID);
  const breadcrumbItems = [
    { name: "Beranda", path: "/" },
    { name: "Telang & Rosella", path: PAGE_PATH },
  ];
  const pageSchema = [
    buildOrganizationSchema(),
    buildBreadcrumbSchema(breadcrumbItems),
    buildFaqSchema(pageFaqs),
    buildCollectionPageSchema({
      name: "Teh Rosella dan Bunga Telang Teras Alami",
      description:
        "Halaman khusus Teras Alami untuk teh rosella dan bunga telang dengan manfaat umum, FAQ natural, dan pilihan paket herbal harian.",
      path: PAGE_PATH,
      products: [telang, rosela].filter(Boolean),
    }),
    telang &&
      buildProductSchema(telang, {
        path: PAGE_PATH,
        description:
          "Teh bunga telang Teras Alami dengan warna biru alami, karakter rasa ringan, dan cocok untuk minuman herbal santai.",
      }),
    rosela &&
      buildProductSchema(rosela, {
        path: PAGE_PATH,
        name: "Teh Rosella Teras Alami",
        description:
          "Teh rosella Teras Alami dengan rasa asam segar, warna merah alami, dan positioning premium herbal untuk gaya hidup sehat.",
      }),
  ].filter(Boolean);
  const isAvailable = (product) =>
    Boolean(product) && (product.is_active ?? product.active ?? true) && product.stock > 0;

  const addProduct = (id, qty = 1, label) => {
    const p = products.find((x) => x.id === id);
    if (!isAvailable(p)) {
      toast.error(`${label || "Produk"} sedang tidak tersedia`);
      return false;
    }
    addToCart(id, qty);
    return true;
  };

  const addBundle = (telangQty, roselaQty, label) => {
    const okT = addProduct(TELANG_ID, telangQty, "Bunga Telang");
    const okR = addProduct(ROSELA_ID, roselaQty, "Rosella");
    if (okT && okR) {
      toast.success(`${label} ditambahkan ke keranjang`);
      openCart();
    }
  };

  return (
    <main className="pb-28 md:pb-0" data-testid="telang-rosella-landing">
      <SEOHead
        title="Teh Rosella dan Bunga Telang Premium Herbal"
        description="Temukan teh rosella dan bunga telang Teras Alami dengan rasa segar, FAQ natural, paket herbal harian, dan informasi yang mudah dipahami Google maupun AI search."
        path={PAGE_PATH}
        schema={pageSchema}
      />

      {/* =====================================================
          SECTION 1 – HERO
      ===================================================== */}
      <section
        className="relative overflow-hidden bg-gradient-to-b from-emerald-50/40 via-white to-white"
        data-testid="tr-hero"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <PublicBreadcrumbs items={breadcrumbItems} />
            <div className="flex flex-wrap gap-2 mb-5">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold border border-emerald-200">
                <Star className="w-3.5 h-3.5" /> Best Seller Herbal Drink
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-100 text-purple-800 text-xs font-semibold border border-purple-200">
                <Leaf className="w-3.5 h-3.5" /> Fresh Natural Taste
              </span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-stone-900 leading-tight">
              Segarnya Herbal Alami untuk{" "}
              <span className="text-emerald-700">Rutinitas Sehat</span>{" "}
              Setiap Hari
            </h1>
            <p className="mt-5 text-lg text-stone-600 leading-relaxed max-w-xl">
              Nikmati <span className="text-purple-700 font-medium">Bunga Telang</span>{" "}
              dan <span className="text-rose-700 font-medium">Teh Rosella</span>{" "}
              pilihan — minuman herbal alami dengan rasa khas, warna cantik, dan
              manfaat ringan untuk mendukung gaya hidup sehat keluarga.
            </p>
            <p className="mt-4 max-w-2xl rounded-2xl border border-emerald-100 bg-white px-5 py-4 text-sm leading-7 text-stone-600">
              Ringkasan halaman: Teras Alami menghadirkan teh rosella dan bunga telang sebagai
              produk herbal alami premium dengan informasi terstruktur, FAQ natural language, serta
              konteks konsumsi yang aman untuk pembaca manusia maupun AI search engine.
            </p>

            <ul className="mt-6 space-y-2.5 text-stone-800">
              {[
                "Bahan herbal pilihan",
                "Warna alami dari bunga asli",
                "Cocok diminum hangat atau dingin",
                "Praktis untuk konsumsi harian",
              ].map((h) => (
                <li key={h} className="flex items-center gap-2.5">
                  <span className="inline-flex w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 items-center justify-center shrink-0">
                    <Check className="w-3.5 h-3.5" />
                  </span>
                  <span className="text-base">{h}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                onClick={() => {
                  document
                    .getElementById("produk-utama")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
                className="h-14 px-6 rounded-xl text-base bg-emerald-600 hover:bg-emerald-700 text-white hover:-translate-y-0.5 transition"
                data-testid="hero-cta-pesan"
              >
                <ShoppingBasket className="w-5 h-5 mr-2" /> Pesan Sekarang
              </Button>
              <Button
                onClick={() => {
                  document
                    .getElementById("manfaat")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
                variant="outline"
                className="h-14 px-6 rounded-xl text-base border-stone-200"
                data-testid="hero-cta-manfaat"
              >
                Lihat Manfaat Produk
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-8 bg-emerald-100/50 rounded-[3rem] blur-3xl"></div>
            <div className="relative grid grid-cols-2 gap-4">
              <div className="relative rounded-3xl overflow-hidden border border-purple-100 shadow-sm aspect-[3/4] bg-stone-100 translate-y-6">
                <img
                  src="https://images.pexels.com/photos/34439025/pexels-photo-34439025.jpeg"
                  alt="Bunga telang premium Teras Alami dengan warna biru alami"
                  className="w-full h-full object-cover"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                />
                <span className="absolute top-3 left-3 bg-purple-600 text-white text-xs px-2.5 py-1 rounded-full font-semibold">
                  Telang
                </span>
              </div>
              <div className="relative rounded-3xl overflow-hidden border border-rose-100 shadow-sm aspect-[3/4] bg-stone-100">
                <img
                  src="https://images.pexels.com/photos/34481087/pexels-photo-34481087.jpeg"
                  alt="Teh rosella premium Teras Alami dengan warna merah alami"
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
                <span className="absolute top-3 left-3 bg-rose-600 text-white text-xs px-2.5 py-1 rounded-full font-semibold">
                  Rosella
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        aria-labelledby="ai-summary-heading"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10"
      >
        <div className="rounded-[2rem] border border-stone-200 bg-white p-6 shadow-sm sm:p-8">
          <h2 id="ai-summary-heading" className="font-display text-2xl font-semibold text-stone-900">
            Poin penting untuk pembeli, Google, dan AI search
          </h2>
          <div className="mt-5 grid gap-4 md:grid-cols-3">
            <div className="rounded-2xl bg-stone-50 p-4">
              <h3 className="font-medium text-stone-900">Brand herbal premium</h3>
              <p className="mt-2 text-sm leading-6 text-stone-600">
                Teras Alami diposisikan sebagai brand herbal modern dengan visual bersih, natural,
                dan terpercaya.
              </p>
            </div>
            <div className="rounded-2xl bg-stone-50 p-4">
              <h3 className="font-medium text-stone-900">Produk utama jelas</h3>
              <p className="mt-2 text-sm leading-6 text-stone-600">
                Halaman ini fokus pada dua entitas produk: teh rosella dan bunga telang, lengkap
                dengan karakter rasa, waktu minum, dan cara penyajian.
              </p>
            </div>
            <div className="rounded-2xl bg-stone-50 p-4">
              <h3 className="font-medium text-stone-900">Informasi natural</h3>
              <p className="mt-2 text-sm leading-6 text-stone-600">
                Semua deskripsi menggunakan bahasa manusia yang natural dan menghindari klaim medis
                berlebihan.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          SECTION 2 – PAIN POINT
      ===================================================== */}
      <section
        className="bg-stone-50 border-y border-stone-100"
        data-testid="tr-pain"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <SectionHeading
            eyebrow="Apa kata Anda?"
            title="Sering Ingin Minum yang Segar, Tapi Tetap Alami?"
            desc="Banyak minuman terasa enak, tetapi terlalu manis dan kurang cocok untuk rutinitas sehat. Bunga Telang dan Rosella hadir sebagai pilihan herbal alami yang segar, ringan, dan mudah dinikmati kapan saja."
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
            {[
              { icon: Coffee, text: "Bosan dengan minuman biasa" },
              { icon: Leaf, text: "Ingin pilihan yang lebih alami" },
              { icon: Flower2, text: "Ingin minuman cantik tanpa pewarna buatan" },
              { icon: Heart, text: "Ingin mulai rutinitas sehat sederhana" },
            ].map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.text}
                  className="bg-white border border-stone-200 rounded-2xl p-5 shadow-sm"
                >
                  <span className="inline-flex w-11 h-11 rounded-xl bg-emerald-50 text-emerald-700 items-center justify-center mb-3">
                    <Icon className="w-5 h-5" />
                  </span>
                  <p className="font-medium text-stone-800">{p.text}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-8 bg-emerald-600 text-white rounded-2xl px-6 py-5 flex items-center gap-3">
            <Sparkles className="w-5 h-5 shrink-0" />
            <p className="font-display text-lg sm:text-xl font-medium">
              Mulai dari segelas herbal alami setiap hari.
            </p>
          </div>
        </div>
      </section>

      {/* =====================================================
          SECTION 3 – PRODUK UTAMA
      ===================================================== */}
      <section
        id="produk-utama"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24"
        data-testid="tr-products"
      >
        <SectionHeading
          eyebrow="Dua produk pilihan"
          title="Pilih Favorit Anda"
          desc="Setiap minuman punya karakter unik — pilih yang paling cocok untuk suasana hari ini."
        />

        <div className="grid md:grid-cols-2 gap-6 lg:gap-8 mt-10">
          {/* Telang */}
          <article className="bg-white border border-purple-100 rounded-3xl overflow-hidden shadow-sm hover-lift flex flex-col">
            <div className="relative aspect-[16/10] bg-purple-50">
              <img
                src="https://images.pexels.com/photos/34439025/pexels-photo-34439025.jpeg"
                alt="Produk bunga telang Teras Alami untuk minuman herbal alami"
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
              <span className="absolute top-4 left-4 bg-purple-600 text-white text-xs px-3 py-1 rounded-full font-semibold">
                Biru Alami
              </span>
            </div>
            <div className="p-6 sm:p-8 flex-1 flex flex-col">
              <h3 className="font-display text-2xl sm:text-3xl font-semibold text-stone-900">
                Bunga Telang
              </h3>
              <p className="mt-2 text-stone-600">
                Bunga telang dikenal dengan warna biru alaminya yang cantik.
                Cocok dijadikan minuman herbal ringan untuk menemani waktu
                santai.
              </p>

              <ul className="mt-5 space-y-2 text-stone-800 text-[15px]">
                {[
                  "Mengandung antioksidan alami",
                  "Cocok untuk relaksasi ringan",
                  "Mendukung fokus dan ketenangan",
                  "Warna berubah jadi ungu saat ditambah lemon",
                  "Cocok diminum sore atau malam hari",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-purple-600 mt-1 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-5 bg-purple-50 rounded-xl p-4 text-sm text-purple-900 border border-purple-100">
                <span className="font-semibold">Karakter rasa: </span>
                Ringan, lembut, netral, mudah dipadukan.
              </div>

              {telang && (
                <div className="mt-6 flex flex-col items-stretch gap-4 sm:flex-row sm:items-end sm:justify-between">
                  <div className="shrink-0">
                    <p className="text-xs text-stone-500">Harga</p>
                    <p className="font-display text-2xl font-semibold text-purple-700">
                      {formatRupiah(telang.price)}
                    </p>
                  </div>
                  <Button
                    onClick={() => {
                      if (addProduct(TELANG_ID, 1, "Bunga Telang")) {
                        toast.success("Bunga Telang ditambahkan ke keranjang");
                        openCart();
                      }
                    }}
                    disabled={!isAvailable(telang)}
                    className="min-h-12 w-full rounded-xl bg-purple-600 px-4 py-3 text-center text-sm leading-snug text-white hover:bg-purple-700 sm:w-auto sm:min-w-[250px] sm:px-5 sm:text-base disabled:bg-stone-200 disabled:text-stone-400"
                    data-testid="add-telang"
                  >
                    <ShoppingBasket className="w-5 h-5 mr-2" />
                    Tambah Telang ke Keranjang
                  </Button>
                </div>
              )}
            </div>
          </article>

          {/* Rosela */}
          <article className="bg-white border border-rose-100 rounded-3xl overflow-hidden shadow-sm hover-lift flex flex-col">
            <div className="relative aspect-[16/10] bg-rose-50">
              <img
                src="https://images.pexels.com/photos/34481087/pexels-photo-34481087.jpeg"
                alt="Produk teh rosella Teras Alami untuk minuman herbal alami"
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
              <span className="absolute top-4 left-4 bg-rose-600 text-white text-xs px-3 py-1 rounded-full font-semibold">
                Merah Alami
              </span>
            </div>
            <div className="p-6 sm:p-8 flex-1 flex flex-col">
              <h3 className="font-display text-2xl sm:text-3xl font-semibold text-stone-900">
                Teh Rosella
              </h3>
              <p className="mt-2 text-stone-600">
                Rosella memiliki warna merah pekat alami dengan rasa asam segar
                yang khas. Cocok untuk minuman pagi atau siang hari.
              </p>

              <ul className="mt-5 space-y-2 text-stone-800 text-[15px]">
                {[
                  "Mengandung vitamin C alami",
                  "Mendukung daya tahan tubuh",
                  "Rasa segar dan ringan",
                  "Cocok untuk menemani aktivitas harian",
                  "Dapat dinikmati hangat atau dingin",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-rose-600 mt-1 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-5 bg-rose-50 rounded-xl p-4 text-sm text-rose-900 border border-rose-100">
                <span className="font-semibold">Karakter rasa: </span>
                Asam segar, fruity, menyegarkan.
              </div>

              {rosela && (
                <div className="mt-6 flex flex-col items-stretch gap-4 sm:flex-row sm:items-end sm:justify-between">
                  <div className="shrink-0">
                    <p className="text-xs text-stone-500">Harga</p>
                    <p className="font-display text-2xl font-semibold text-rose-700">
                      {formatRupiah(rosela.price)}
                    </p>
                  </div>
                  <Button
                    onClick={() => {
                      if (addProduct(ROSELA_ID, 1, "Rosella")) {
                        toast.success("Rosella ditambahkan ke keranjang");
                        openCart();
                      }
                    }}
                    disabled={!isAvailable(rosela)}
                    className="min-h-12 w-full rounded-xl bg-rose-600 px-4 py-3 text-center text-sm leading-snug text-white hover:bg-rose-700 sm:w-auto sm:min-w-[250px] sm:px-5 sm:text-base disabled:bg-stone-200 disabled:text-stone-400"
                    data-testid="add-rosela"
                  >
                    <ShoppingBasket className="w-5 h-5 mr-2" />
                    Tambah Rosella ke Keranjang
                  </Button>
                </div>
              )}
            </div>
          </article>
        </div>
      </section>

      {/* =====================================================
          SECTION 4 – EDUKASI MANFAAT
      ===================================================== */}
      <section
        id="manfaat"
        className="bg-stone-50 border-y border-stone-100"
        data-testid="tr-edu"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <SectionHeading
            eyebrow="Edukasi singkat"
            title="Kenapa Telang dan Rosella Banyak Dipilih?"
            tone="purple"
          />

          <div className="grid md:grid-cols-2 gap-6 mt-10">
            <div className="bg-white border border-purple-100 rounded-2xl p-6 sm:p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-flex w-11 h-11 rounded-xl bg-purple-100 text-purple-700 items-center justify-center">
                  <Flower2 className="w-5 h-5" />
                </span>
                <h3 className="font-display text-xl sm:text-2xl font-semibold text-stone-900">
                  Bunga Telang
                </h3>
              </div>
              <p className="text-stone-600 leading-relaxed">
                Bunga telang dikenal memiliki kandungan{" "}
                <span className="font-medium text-purple-700">anthocyanin</span>
                , pigmen alami yang memberi warna biru khas. Kandungan ini sering
                dikaitkan dengan aktivitas antioksidan yang membantu tubuh melawan
                radikal bebas.
              </p>
              <ul className="mt-5 space-y-2 text-stone-800">
                {[
                  "Warna biru alami dari bunga telang",
                  "Dikenal sebagai sumber antioksidan alami",
                  "Cocok untuk minuman relaksasi",
                  "Bisa dikreasikan dengan lemon, madu, atau es batu",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 text-purple-600 mt-0.5 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white border border-rose-100 rounded-2xl p-6 sm:p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-flex w-11 h-11 rounded-xl bg-rose-100 text-rose-700 items-center justify-center">
                  <Citrus className="w-5 h-5" />
                </span>
                <h3 className="font-display text-xl sm:text-2xl font-semibold text-stone-900">
                  Teh Rosella
                </h3>
              </div>
              <p className="text-stone-600 leading-relaxed">
                Rosella dikenal dengan rasa asam segar dan warna merah alaminya.
                Rosella sering dipilih karena mengandung{" "}
                <span className="font-medium text-rose-700">vitamin C alami</span>{" "}
                dan senyawa antioksidan.
              </p>
              <ul className="mt-5 space-y-2 text-stone-800">
                {[
                  "Warna merah alami tanpa pewarna buatan",
                  "Rasa asam segar yang khas",
                  "Mengandung vitamin C alami",
                  "Cocok untuk minuman segar harian",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 text-rose-600 mt-0.5 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <p className="mt-6 text-sm text-stone-500 flex items-start gap-2">
            <Info className="w-4 h-4 mt-0.5 shrink-0" />
            Manfaat dapat berbeda pada setiap orang dan sebaiknya dikonsumsi
            secara wajar.
          </p>
        </div>
      </section>

      {/* =====================================================
          SECTION 5 – COMPARISON
      ===================================================== */}
      <section
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24"
        data-testid="tr-compare"
      >
        <SectionHeading
          eyebrow="Perbandingan cepat"
          title="Telang vs Rosella"
          desc="Keduanya alami dan sehat — mana yang lebih cocok untuk hari Anda?"
        />

        <div className="mt-10 bg-white border border-stone-200 rounded-2xl overflow-hidden shadow-sm">
          {[
            { label: "Warna", t: "Biru alami", r: "Merah alami", iconT: Droplets, iconR: Droplets },
            { label: "Rasa", t: "Ringan / netral", r: "Asam segar", iconT: Leaf, iconR: Citrus },
            { label: "Cocok diminum", t: "Sore / malam", r: "Pagi / siang", iconT: Moon, iconR: Sun },
            { label: "Karakter", t: "Lembut & menenangkan", r: "Segar & membangkitkan", iconT: Heart, iconR: Zap },
            { label: "Kreasi", t: "Lemon, madu, es batu", r: "Madu, jeruk, jahe", iconT: Sparkles, iconR: Sparkles },
          ].map((row, idx) => {
            const IT = row.iconT;
            const IR = row.iconR;
            return (
              <div
                key={row.label}
                className={`grid grid-cols-1 md:grid-cols-[160px_1fr_1fr] ${
                  idx > 0 ? "border-t border-stone-100" : ""
                }`}
              >
                <div className="px-5 py-4 bg-stone-50/60 font-medium text-stone-700 border-b md:border-b-0 md:border-r border-stone-100">
                  {row.label}
                </div>
                <div className="px-5 py-4 border-b md:border-b-0 md:border-r border-stone-100 flex items-start gap-3">
                  <span className="inline-flex w-8 h-8 rounded-lg bg-purple-50 text-purple-700 items-center justify-center shrink-0">
                    <IT className="w-4 h-4" />
                  </span>
                  <div>
                    <p className="text-xs text-purple-700 font-semibold">Telang</p>
                    <p className="text-stone-800">{row.t}</p>
                  </div>
                </div>
                <div className="px-5 py-4 flex items-start gap-3">
                  <span className="inline-flex w-8 h-8 rounded-lg bg-rose-50 text-rose-700 items-center justify-center shrink-0">
                    <IR className="w-4 h-4" />
                  </span>
                  <div>
                    <p className="text-xs text-rose-700 font-semibold">Rosella</p>
                    <p className="text-stone-800">{row.r}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-6 flex flex-col sm:flex-row items-center gap-4 bg-gradient-to-r from-purple-50 to-rose-50 border border-stone-200 rounded-2xl p-5">
          <p className="text-stone-800 font-medium flex-1 text-center sm:text-left">
            Bingung pilih yang mana? Ambil paket kombinasi Telang + Rosella.
          </p>
          <Button
            onClick={() => {
              document
                .getElementById("paket")
                ?.scrollIntoView({ behavior: "smooth" });
            }}
            className="h-12 rounded-xl bg-stone-900 hover:bg-stone-800 text-white px-5"
            data-testid="goto-bundle"
          >
            Lihat Paket <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* =====================================================
          SECTION 6 – BUNDLE HARD SELLING
      ===================================================== */}
      <section
        id="paket"
        className="bg-gradient-to-b from-emerald-50/40 to-white border-y border-stone-100"
        data-testid="tr-bundle"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <SectionHeading
            eyebrow="Lebih hemat"
            title="Paket Herbal Harian Lebih Hemat"
            desc="Lebih praktis punya stok herbal di rumah, tinggal seduh kapan saja."
          />

          {(() => {
            const unit = (telang?.price || 0) + (rosela?.price || 0);
            const bundles = [
              {
                key: "coba",
                title: "Paket Coba",
                desc: "Cocok untuk pemula",
                items: "1 Telang + 1 Rosella",
                qty: [1, 1],
                price: unit * 1,
                cta: "Pilih Paket Coba",
                tone: "border-stone-200",
                badge: null,
                testId: "bundle-coba",
              },
              {
                key: "keluarga",
                title: "Paket Keluarga",
                desc: "Hemat untuk keluarga",
                items: "2 Telang + 2 Rosella",
                qty: [2, 2],
                price: unit * 2,
                cta: "Pilih Paket Keluarga",
                tone: "border-emerald-500 ring-2 ring-emerald-100",
                badge: "Paling Banyak Dipilih",
                testId: "bundle-keluarga",
              },
              {
                key: "stok",
                title: "Paket Stok Sehat",
                desc: "Stok herbal di rumah",
                items: "3 Telang + 3 Rosella",
                qty: [3, 3],
                price: unit * 3,
                cta: "Pilih Paket Stok Sehat",
                tone: "border-stone-200",
                badge: null,
                testId: "bundle-stok",
              },
            ];
            return (
              <div className="grid md:grid-cols-3 gap-5 mt-10">
                {bundles.map((b) => (
                  <div
                    key={b.key}
                    className={`relative bg-white rounded-2xl p-6 shadow-sm border ${b.tone} flex flex-col`}
                    data-testid={b.testId}
                  >
                    {b.badge && (
                      <span className="absolute -top-3 left-6 bg-emerald-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                        {b.badge}
                      </span>
                    )}
                    <span className="inline-flex w-11 h-11 rounded-xl bg-emerald-50 text-emerald-700 items-center justify-center">
                      <Package className="w-5 h-5" />
                    </span>
                    <h3 className="font-display text-xl font-semibold text-stone-900 mt-4">
                      {b.title}
                    </h3>
                    <p className="text-stone-500 text-sm">{b.desc}</p>
                    <p className="mt-4 text-stone-800 font-medium">{b.items}</p>
                    <p className="mt-1 font-display text-2xl font-semibold text-emerald-700">
                      {formatRupiah(b.price)}
                    </p>
                    <Button
                      onClick={() => addBundle(b.qty[0], b.qty[1], b.title)}
                      className="mt-5 h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid={`${b.testId}-cta`}
                    >
                      {b.cta}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      </section>

      {/* =====================================================
          SECTION 7 – CARA PENYAJIAN
      ===================================================== */}
      <section
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24"
        data-testid="tr-serving"
      >
        <SectionHeading
          eyebrow="Cara penyajian"
          title="Mudah Diseduh, Enak Dinikmati"
          desc="Tanpa ribet, cocok untuk rutinitas pagi, sore, atau malam."
        />

        <ol className="mt-10 grid md:grid-cols-4 gap-4">
          {[
            { t: "Siapkan bunga", d: "Masukkan bunga telang atau rosella secukupnya" },
            { t: "Seduh", d: "Seduh dengan air panas 3–5 menit" },
            { t: "Tambah kreasi", d: "Tambahkan madu atau lemon sesuai selera" },
            { t: "Sajikan", d: "Nikmati hangat atau dingin" },
          ].map((s, i) => (
            <li
              key={s.t}
              className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm relative"
            >
              <span className="absolute -top-4 left-6 w-9 h-9 rounded-full bg-emerald-600 text-white font-display text-lg font-semibold flex items-center justify-center shadow">
                {i + 1}
              </span>
              <span className="inline-flex w-11 h-11 rounded-xl bg-stone-100 text-stone-700 items-center justify-center mt-4">
                {i === 0 && <Flower2 className="w-5 h-5" />}
                {i === 1 && <Flame className="w-5 h-5" />}
                {i === 2 && <Citrus className="w-5 h-5" />}
                {i === 3 && <Snowflake className="w-5 h-5" />}
              </span>
              <p className="font-display text-lg font-semibold text-stone-900 mt-3">
                {s.t}
              </p>
              <p className="text-stone-600 text-sm mt-1">{s.d}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* =====================================================
          SECTION 8 – TRUST BUILDER
      ===================================================== */}
      <section
        className="bg-stone-50 border-y border-stone-100"
        data-testid="tr-trust"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <SectionHeading
            eyebrow="Kepercayaan"
            title="Kenapa Produk Ini Layak Anda Pilih?"
            desc="Kami menghadirkan produk herbal yang tidak hanya cantik dilihat, tetapi juga nyaman dinikmati setiap hari."
          />

          <div className="mt-8 grid grid-cols-1 gap-4 sm:mt-10 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { icon: Leaf, t: "Herbal pilihan berkualitas" },
              { icon: Flower2, t: "Warna alami dari bunga asli" },
              { icon: ShieldCheck, t: "Diproses bersih dan higienis" },
              { icon: Heart, t: "Cocok untuk keluarga" },
              { icon: Snowflake, t: "Bisa diminum hangat atau dingin" },
              { icon: Sparkles, t: "Rasa ringan dan mudah diterima" },
            ].map((it) => {
              const I = it.icon;
              return (
                <div
                  key={it.t}
                  className="flex items-start gap-4 rounded-2xl border border-stone-200 bg-white p-4 shadow-sm sm:p-5"
                >
                  <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700 sm:h-12 sm:w-12">
                    <I className="h-5 w-5" />
                  </span>
                  <p className="max-w-[18ch] text-base font-medium leading-snug text-stone-800 sm:max-w-none sm:text-lg">
                    {it.t}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* =====================================================
          SECTION 9 – TESTIMONIAL
      ===================================================== */}
      <section
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24"
        data-testid="tr-testi"
      >
        <SectionHeading
          eyebrow="Kata mereka"
          title="Cerita Pelanggan Kami"
          tone="rose"
        />

        <div className="grid md:grid-cols-3 gap-5 mt-10">
          {[
            {
              name: "Bu Rini",
              city: "Bandung",
              quote:
                "Telangnya cantik banget warnanya, anak-anak juga penasaran waktu berubah jadi ungu.",
            },
            {
              name: "Pak Bowo",
              city: "Surabaya",
              quote:
                "Rosellanya segar, enak diminum dingin siang hari.",
            },
            {
              name: "Mbak Dina",
              city: "Yogyakarta",
              quote:
                "Saya suka karena rasanya ringan dan tidak terlalu manis.",
            },
          ].map((t) => (
            <figure
              key={t.name}
              className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm flex flex-col"
            >
              <div className="flex items-center gap-1 text-amber-500 mb-3">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} className="w-4 h-4 fill-current" />
                ))}
              </div>
              <blockquote className="text-stone-800 leading-relaxed flex-1">
                “{t.quote}”
              </blockquote>
              <figcaption className="mt-4 text-sm text-stone-500">
                <span className="font-semibold text-stone-900">{t.name}</span> ·{" "}
                {t.city}
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* =====================================================
          SECTION 10 – FAQ
      ===================================================== */}
      <section
        className="bg-stone-50 border-y border-stone-100"
        data-testid="tr-faq"
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <SectionHeading
            eyebrow="Tanya jawab"
            title="Hal yang Sering Ditanyakan"
          />

          <Accordion
            type="single"
            collapsible
            className="mt-8 bg-white border border-stone-200 rounded-2xl shadow-sm divide-y divide-stone-100 overflow-hidden"
          >
            {pageFaqs.map((f, i) => (
              <AccordionItem
                key={i}
                value={`q${i}`}
                className="border-0 px-5 sm:px-6"
              >
                <AccordionTrigger
                  className="text-left text-base sm:text-lg font-medium text-stone-900 hover:no-underline py-4"
                  data-testid={`tr-faq-q-${i}`}
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-stone-600 text-base pb-4 leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* =====================================================
          SECTION 11 – CTA FINAL
      ===================================================== */}
      <section className="relative overflow-hidden" data-testid="tr-cta-final">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 text-center">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-sm font-medium border border-emerald-100">
            <Clock3 className="w-4 h-4" /> Stok terbatas mengikuti ketersediaan bunga pilihan
          </span>
          <h2 className="font-display text-3xl sm:text-5xl font-semibold text-stone-900 mt-4 leading-tight">
            Mulai Rutinitas Sehat dari{" "}
            <span className="text-emerald-700">Minuman yang Lebih Alami</span>
          </h2>
          <p className="text-stone-600 text-lg mt-4 max-w-2xl mx-auto">
            Pilih Telang, Rosella, atau paket kombinasi untuk stok herbal
            keluarga di rumah.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button
              onClick={() => {
                document
                  .getElementById("produk-utama")
                  ?.scrollIntoView({ behavior: "smooth" });
              }}
              className="h-14 px-6 rounded-xl text-base bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="cta-final-pesan"
            >
              <ShoppingBasket className="w-5 h-5 mr-2" /> Pesan Sekarang
            </Button>
            <Button
              onClick={() => {
                document
                  .getElementById("paket")
                  ?.scrollIntoView({ behavior: "smooth" });
              }}
              variant="outline"
              className="h-14 px-6 rounded-xl text-base border-stone-200"
              data-testid="cta-final-paket"
            >
              Ambil Paket Hemat
            </Button>
          </div>
        </div>
      </section>

      {/* =====================================================
          SECTION 12 – DISCLAIMER
      ===================================================== */}
      <section className="bg-stone-100 border-t border-stone-200" data-testid="tr-disclaimer">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 text-center">
          <p className="text-sm text-stone-600 leading-relaxed">
            <Info className="w-4 h-4 inline-block mr-1 -mt-0.5" />
            Produk ini adalah minuman herbal alami dan bukan obat atau pengganti
            pengobatan medis. Hasil dan manfaat dapat berbeda pada setiap
            individu. Konsumsi secara wajar dan konsultasikan dengan tenaga
            kesehatan jika memiliki kondisi khusus.
          </p>
          <div className="mt-5">
            <Link
              to="/"
              className="inline-flex items-center text-emerald-700 hover:text-emerald-800 font-medium"
            >
              Kembali ke Beranda <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default TelangRosellaLandingPage;
